import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { endOfMonth, outputDateToApi, parseISO } from '@sequencehq/utils/dates'
import { getStorageItem, setStorageItem } from 'lib/utils/storageUtils'
import type {
  RevenueWaterfallQueryParams,
  RevenueWaterfallGrouping
} from '../content/RevenueWaterfallTable'

const STORAGE_KEY = 'revenueWaterfall.dateFilters'
const DATE_FILTER_KEYS = ['bookedFrom', 'bookedTo', 'recognizedUpTo'] as const
type DateFilterKey = (typeof DATE_FILTER_KEYS)[number]

const TO_DATE_FILTER_KEYS: ReadonlyArray<DateFilterKey> = [
  'bookedTo',
  'recognizedUpTo'
]
const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ENTITY_FILTER_KEYS = ['customerId', 'productId'] as const
const GROUPING_PREFERENCE: RevenueWaterfallGrouping[] = [
  'CUSTOMER',
  'PRODUCT',
  'MONTH'
]

// Backwards compat: bookedTo and recognizedUpTo previously stored
// start-of-month values (e.g. "2025-12-01") that the API silently treated as
// "up to Dec 1". Stored values now represent end-of-month; convert legacy
// values on read so saved filters and bookmarked URLs keep working.
const isLegacyStartOfMonthValue = (value: string) =>
  parseISO(value).getDate() === 1

const normalizeDateFilterValue = (
  key: DateFilterKey,
  value: string
): string => {
  if (TO_DATE_FILTER_KEYS.includes(key) && isLegacyStartOfMonthValue(value)) {
    return outputDateToApi(endOfMonth(parseISO(value)))
  }
  return value
}

const normalizeStoredDateFilters = (
  stored: Partial<RevenueWaterfallQueryParams>
): Partial<RevenueWaterfallQueryParams> => {
  const normalized: Partial<RevenueWaterfallQueryParams> = { ...stored }
  DATE_FILTER_KEYS.forEach(key => {
    const value = normalized[key]
    if (typeof value === 'string') {
      normalized[key] = normalizeDateFilterValue(key, value)
    }
  })
  return normalized
}

const pickDateFilters = (filters: RevenueWaterfallQueryParams) =>
  DATE_FILTER_KEYS.reduce(
    (acc, key) => {
      acc[key] = filters[key]
      return acc
    },
    {} as Record<DateFilterKey, string>
  )

const extractDateFiltersFromSearchParams = (
  searchParams: URLSearchParams
): Partial<RevenueWaterfallQueryParams> | null => {
  const result: Partial<RevenueWaterfallQueryParams> = {}

  DATE_FILTER_KEYS.forEach(key => {
    const value = searchParams.get(key)
    if (value && DATE_VALUE_PATTERN.test(value)) {
      result[key] = normalizeDateFilterValue(key, value)
    }
  })

  ENTITY_FILTER_KEYS.forEach(key => {
    const value = searchParams.get(key)
    if (value) {
      result[key] = value
    }
  })

  return Object.keys(result).length ? result : null
}

const mergeDateFilters = (
  base: RevenueWaterfallQueryParams,
  overrides: Partial<RevenueWaterfallQueryParams> | null
) => ({
  ...base,
  ...(overrides ?? {})
})

export const DEFAULT_REVENUE_WATERFALL_PARAMS: RevenueWaterfallQueryParams = {
  bookedFrom: '2024-01-01',
  bookedTo: '2025-12-31',
  recognizedUpTo: '2025-12-31',

  currency: 'USD',
  groupBy: 'CUSTOMER',
  subGroupBy: 'PRODUCT',
  sortBy: 'label',
  sortDirection: 'ASC'
}

export const useRevenueWaterfallTableParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [groupBy, setGroupBy] = useState(
    DEFAULT_REVENUE_WATERFALL_PARAMS.groupBy
  )
  const [subGroupBy, setSubGroupBy] = useState(
    DEFAULT_REVENUE_WATERFALL_PARAMS.subGroupBy
  )

  const storedDateFiltersRef =
    useRef<Partial<RevenueWaterfallQueryParams> | null>()

  if (typeof storedDateFiltersRef.current === 'undefined') {
    const stored =
      typeof window === 'undefined'
        ? null
        : getStorageItem<Partial<RevenueWaterfallQueryParams> | null>(
            STORAGE_KEY,
            null,
            window.localStorage
          )

    storedDateFiltersRef.current = stored
      ? normalizeStoredDateFilters(stored)
      : null
  }

  const searchParamsKey = searchParams.toString()

  const dateFilters = useMemo(() => {
    const fromParams = extractDateFiltersFromSearchParams(searchParams)

    if (fromParams) {
      return mergeDateFilters(DEFAULT_REVENUE_WATERFALL_PARAMS, fromParams)
    }

    if (storedDateFiltersRef.current) {
      return mergeDateFilters(
        DEFAULT_REVENUE_WATERFALL_PARAMS,
        storedDateFiltersRef.current
      )
    }

    return { ...DEFAULT_REVENUE_WATERFALL_PARAMS }
  }, [searchParamsKey])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return
    }

    const paramsSnapshot = new URLSearchParams(searchParamsKey)
    const hasMissingDateParam = DATE_FILTER_KEYS.some(
      key => !paramsSnapshot.get(key)
    )

    if (hasMissingDateParam) {
      setSearchParams(
        params => {
          DATE_FILTER_KEYS.forEach(key => {
            params.set(key, dateFilters[key])
          })
          return params
        },
        { replace: true }
      )
    }

    storedDateFiltersRef.current = pickDateFilters(dateFilters)
    setStorageItem(
      STORAGE_KEY,
      storedDateFiltersRef.current,
      window.localStorage
    )
  }, [dateFilters, searchParamsKey, setSearchParams])

  const filters = {
    ...dateFilters,
    groupBy,
    subGroupBy
  }

  const updateSearchParams = useCallback(
    (next: RevenueWaterfallQueryParams) => {
      setSearchParams(
        params => {
          DATE_FILTER_KEYS.forEach(key => {
            params.set(key, next[key])
          })
          ENTITY_FILTER_KEYS.forEach(key => {
            if (next[key]) {
              params.set(key, next[key]!)
            } else {
              params.delete(key)
            }
          })
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setFilters = useCallback(
    (
      nextOrUpdater:
        | RevenueWaterfallQueryParams
        | ((prev: RevenueWaterfallQueryParams) => RevenueWaterfallQueryParams)
    ) => {
      const previous = {
        ...dateFilters,
        groupBy,
        subGroupBy
      }

      let next =
        typeof nextOrUpdater === 'function'
          ? nextOrUpdater(previous)
          : nextOrUpdater

      if (next.groupBy === next.subGroupBy) {
        const preferredFallbacks = [previous.groupBy, ...GROUPING_PREFERENCE]

        const resolvedCandidate = preferredFallbacks.find(
          option => option && option !== next.groupBy
        )

        const alternative = GROUPING_PREFERENCE.find(
          option => option !== next.groupBy
        )

        const resolvedSubGrouping = (resolvedCandidate ||
          alternative ||
          DEFAULT_REVENUE_WATERFALL_PARAMS.subGroupBy) as RevenueWaterfallGrouping

        if (resolvedSubGrouping !== next.groupBy) {
          next = {
            ...next,
            subGroupBy: resolvedSubGrouping
          }
        }
      }

      if (next.groupBy !== groupBy) {
        setGroupBy(next.groupBy)
      }

      if (next.subGroupBy !== subGroupBy) {
        setSubGroupBy(next.subGroupBy)
      }

      const dateOnlyFilters = pickDateFilters(next)
      const hasDateChanges = DATE_FILTER_KEYS.some(
        key => dateFilters[key] !== dateOnlyFilters[key]
      )

      const hasEntityChanges = ENTITY_FILTER_KEYS.some(
        key => previous[key] !== next[key]
      )

      if (hasDateChanges || hasEntityChanges) {
        if (hasDateChanges) {
          storedDateFiltersRef.current = dateOnlyFilters

          if (typeof window !== 'undefined' && window.localStorage) {
            setStorageItem(STORAGE_KEY, dateOnlyFilters, window.localStorage)
          }
        }

        updateSearchParams({ ...next, ...dateOnlyFilters })
      }
    },
    [dateFilters, groupBy, subGroupBy, updateSearchParams]
  )

  const resetFilters = useCallback(() => {
    const { groupBy: defaultGrouping, subGroupBy: defaultSubGrouping } =
      DEFAULT_REVENUE_WATERFALL_PARAMS

    setGroupBy(defaultGrouping)
    setSubGroupBy(defaultSubGrouping)

    const dateDefaults = pickDateFilters(DEFAULT_REVENUE_WATERFALL_PARAMS)
    storedDateFiltersRef.current = dateDefaults

    if (typeof window !== 'undefined' && window.localStorage) {
      setStorageItem(STORAGE_KEY, dateDefaults, window.localStorage)
    }

    updateSearchParams(DEFAULT_REVENUE_WATERFALL_PARAMS)
  }, [updateSearchParams])

  return {
    filters,
    setFilters,
    resetFilters
  }
}
